// components/Navbar.js - Theme-aware navbar
import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { getGridProjects } from "../data/projects";

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrollPosition, setScrollPosition] = useState(0);
  const { pathname } = useLocation();

  // Track scroll position for background transparency
  useEffect(() => {
    const handleScroll = () => {
      setScrollPosition(window.scrollY);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    // Prevent body scrolling when menu is open
    if (!isMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
  };

  // Close menu when clicking a link on mobile
  const closeMenu = () => {
    setIsMenuOpen(false);
    document.body.style.overflow = "auto";
  };

  // Extra breathing room above the nav on the About page (only at the top)
  // Pages that use the centered, stacked header with space above it
  const isStacked =
    pathname === "/" ||
    pathname === "/work" ||
    pathname.startsWith("/project") ||
    pathname.startsWith("/photography");
  const roomyTop = isStacked && scrollPosition <= 20;

  // Use inline style for navbar background to ensure it uses theme color
  const navbarStyle = {
    backgroundColor:
      scrollPosition > 20 || isMenuOpen
        ? "var(--color-background)"
        : "transparent",
    boxShadow:
      scrollPosition > 20 || isMenuOpen
        ? "0 4px 6px -1px rgba(0, 0, 0, 0.1)"
        : "none",
  };

  return (
    <nav
      className={`fixed top-0 w-full z-50 px-4 transition-all duration-300 ${
        roomyTop ? "pt-6 pb-3 md:pt-20" : "py-3"
      }`}
      style={navbarStyle}
    >
      <div
        className={`max-w-7xl mx-auto relative flex justify-between items-center ${
          isStacked ? "md:flex-col md:gap-5" : ""
        }`}
      >
        <Link
          to="/"
          className="text-xl text-white font-display tracking-wider"
          onClick={closeMenu}
        >
          Abdullah Kokash
        </Link>

        {/* Desktop Navigation */}
        <div
          className={`hidden md:flex space-x-16 font-sans text-sm ${
            isStacked ? "" : "absolute left-1/2 -translate-x-1/2"
          }`}
        >
          {[
            {
              to: "/work",
              label: "Work",
              active: pathname === "/work" || pathname.startsWith("/project"),
            },
            {
              to: "/photography",
              label: "Photography",
              active: pathname.startsWith("/photography"),
            },
            { to: "/", label: "About", active: pathname === "/" },
          ].map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={`text-white hover:opacity-70 transition-opacity duration-300 pb-0.5 border-b ${
                item.active ? "border-current" : "border-transparent"
              }`}
            >
              {item.label}
            </Link>
          ))}
        </div>

        {/* Mobile menu button */}
        <button
          className="md:hidden text-white focus:outline-none"
          onClick={toggleMenu}
          aria-label="Toggle menu"
        >
          {isMenuOpen ? (
            // Close icon
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-8 w-8"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          ) : (
            // Hamburger icon
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-8 w-8"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16m-7 6h7"
              />
            </svg>
          )}
        </button>
      </div>

      {/* Mobile Navigation Menu */}
      <div
        className={`fixed inset-0 z-40 transition-transform duration-300 pt-20 ${
          isMenuOpen ? "transform translate-x-0" : "transform translate-x-full"
        } md:hidden`}
        style={{ backgroundColor: "var(--color-background)" }}
      >
        <div className="flex flex-col items-center text-xl space-y-8 mt-10">
          <Link
            to="/work"
            className="text-white hover:text-red-500 transition-colors duration-300 w-full text-center py-4"
            onClick={closeMenu}
          >
            WORK
          </Link>

          {/* Mobile project list */}
          <div className="w-full px-4">
            <div className="text-center mb-4 text-red-500">PROJECTS</div>
            <div className="max-h-64 overflow-y-auto space-y-2">
              {getGridProjects().map((project) => (
                <Link
                  key={project.id}
                  to={`/project/${project.id}`}
                  className="flex items-center p-2 hover:bg-gray-900 rounded"
                  onClick={closeMenu}
                >
                  <div className="w-10 h-10 mr-3">
                    <img
                      src={project.thumbnail}
                      alt={project.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <span className="text-white">{project.title}</span>
                </Link>
              ))}
            </div>
          </div>

          <Link
            to="/photography"
            className="text-white hover:text-red-500 transition-colors duration-300 w-full text-center py-4"
            onClick={closeMenu}
          >
            PHOTOGRAPHY
          </Link>
          <Link
            to="/"
            className="text-white hover:text-red-500 transition-colors duration-300 w-full text-center py-4"
            onClick={closeMenu}
          >
            ABOUT
          </Link>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
